/**
 * Surface 2 — which email domains join this organization automatically.
 *
 * The highest-consequence surface in the panel, and the reason detach confirms: a domain is not a
 * property of one account, it is the rule that maps EVERY user at that address to this client. Detaching
 * it drops all of them to no organization at once, and attaching one that belongs elsewhere would move
 * them all to a different client. Hence the consequence text, and hence 03c-ii built revert here first.
 */
import { useState } from 'react';

import { ConfirmInline } from './ConfirmInline';
import { Row } from './Row';
import type { DomainRow } from '../types';

interface DomainsSurfaceProps {
  domains: DomainRow[];
  orgName: string;
  onAttach: (domain: string) => Promise<string | null>;
  onDetach: (domain: string) => Promise<string | null>;
  busy: boolean;
}

export function DomainsSurface({
  domains,
  orgName,
  onAttach,
  onDetach,
  busy,
}: DomainsSurfaceProps) {
  const [pendingDetach, setPendingDetach] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const attach = async (event: React.FormEvent) => {
    event.preventDefault();
    const domain = draft.trim();
    if (!domain) return;
    // The server's refusal is the message. A 409 names the client already holding the domain and a 422
    // explains a public-provider refusal; replacing either with "could not attach" throws away the only
    // thing that tells the operator what to do next.
    const failure = await onAttach(domain);
    setError(failure);
    if (!failure) setDraft('');
  };

  return (
    <section className="border-t border-gray-100 px-4 py-4 first:border-t-0">
      <h3 className="text-sm font-semibold text-gray-900">Email domains</h3>
      <p className="mb-2 mt-0.5 text-xs text-gray-500">
        Anyone signing in at these addresses becomes part of this organization.
      </p>

      {domains.length === 0 ? (
        <p className="py-1 text-xs text-gray-500">
          No domains. Users are matched individually until one is attached.
        </p>
      ) : (
        domains.map((d) => (
          <Row
            key={d.domain}
            source={d.source}
            createdByEmail={d.created_by_email}
            actions={
              <button
                type="button"
                onClick={() => setPendingDetach(d.domain)}
                className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50"
              >
                Detach
              </button>
            }
            below={
              pendingDetach === d.domain ? (
                <ConfirmInline
                  action="Detach"
                  busy={busy}
                  consequence={
                    <>
                      Detach <b>{d.domain}</b>? Every user signing in at that address loses{' '}
                      <b>{orgName}</b>&apos;s access and falls back to no organization.
                    </>
                  }
                  onConfirm={async () => {
                    const failure = await onDetach(d.domain);
                    setError(failure);
                    setPendingDetach(null);
                  }}
                  onCancel={() => setPendingDetach(null)}
                />
              ) : null
            }
          >
            <span className="font-mono text-sm">{d.domain}</span>
          </Row>
        ))
      )}

      <form onSubmit={attach} className="mt-3 flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="client-domain.com"
          aria-label="Domain to attach"
          className="w-64 rounded border border-gray-300 px-2 py-1 text-sm"
        />
        <button
          type="submit"
          disabled={busy || !draft.trim()}
          className="rounded bg-teal-700 px-3 py-1 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-40"
        >
          Attach domain
        </button>
      </form>

      {error && (
        <p role="alert" className="mt-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {error}
        </p>
      )}
    </section>
  );
}

export default DomainsSurface;
